import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const TransactionContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TransactionProvider = ({ children }) => {
  const { getAuthHeaders, isAuthenticated, user } = useAuth();
  const [recettes, setRecettes] = useState([]);
  const [depenses, setDepenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [urssafNotifications, setUrssafNotifications] = useState([]);
  const [urssafSettings, setUrssafSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);

  // Les nouveaux utilisateurs commencent avec un tableau de bord vide
  // Pas de données de démonstration automatiques

  const fetchRecettes = useCallback(async (annee = selectedYear, mois = null) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/recettes?annee=${annee}`;
      if (mois) url += `&mois=${mois}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setRecettes(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching recettes:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, selectedYear]);

  const fetchDepenses = useCallback(async (annee = selectedYear, mois = null) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/depenses?annee=${annee}`;
      if (mois) url += `&mois=${mois}`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setDepenses(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching depenses:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, selectedYear]);

  const fetchSummary = useCallback(async (annee = selectedYear) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/summary/${annee}`, { 
        headers: getAuthHeaders() 
      });
      setSummary(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, selectedYear]);

  const fetchComparison = useCallback(async (annee = selectedYear) => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API_URL}/api/comparison/${annee}`, { 
        headers: getAuthHeaders() 
      });
      setComparison(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching comparison:', error);
      return null;
    }
  }, [isAuthenticated, getAuthHeaders, selectedYear]);

  const fetchUrssafNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API_URL}/api/urssaf/notifications`, { 
        headers: getAuthHeaders() 
      });
      setUrssafNotifications(response.data.notifications || []);
      setUrssafSettings(response.data.settings);
      return response.data;
    } catch (error) {
      console.error('Error fetching URSSAF notifications:', error);
      return null;
    }
  }, [isAuthenticated, getAuthHeaders]);

  const updateUrssafSettings = async (settings) => {
    const response = await axios.put(`${API_URL}/api/urssaf/settings`, settings, { 
      headers: getAuthHeaders() 
    });
    await fetchUrssafNotifications();
    return response.data;
  };

  const markUrssafDeclared = async (notificationId) => {
    const response = await axios.post(
      `${API_URL}/api/urssaf/mark-declared/${notificationId}`, 
      {}, 
      { headers: getAuthHeaders() }
    );
    await fetchUrssafNotifications();
    return response.data;
  };

  const createRecette = async (data) => {
    const response = await axios.post(`${API_URL}/api/recettes`, data, { 
      headers: getAuthHeaders() 
    });
    return response.data;
  };

  const updateRecette = async (id, data) => {
    const response = await axios.put(`${API_URL}/api/recettes/${id}`, data, { 
      headers: getAuthHeaders() 
    });
    return response.data;
  };

  const deleteRecette = async (id) => {
    await axios.delete(`${API_URL}/api/recettes/${id}`, { 
      headers: getAuthHeaders() 
    });
  };

  const createDepense = async (data) => {
    const response = await axios.post(`${API_URL}/api/depenses`, data, { 
      headers: getAuthHeaders() 
    });
    return response.data;
  };

  const updateDepense = async (id, data) => {
    const response = await axios.put(`${API_URL}/api/depenses/${id}`, data, { 
      headers: getAuthHeaders() 
    });
    return response.data;
  };

  const deleteDepense = async (id) => {
    await axios.delete(`${API_URL}/api/depenses/${id}`, { 
      headers: getAuthHeaders() 
    });
  };

  const exportCSV = async (annee = selectedYear) => {
    const response = await axios.get(`${API_URL}/api/export/csv/${annee}`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `livre_recettes_${annee}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const exportJSON = async (annee = selectedYear) => {
    const response = await axios.get(`${API_URL}/api/export/json/${annee}`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `livre_recettes_${annee}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <TransactionContext.Provider value={{
      recettes,
      depenses,
      summary,
      comparison,
      urssafNotifications,
      urssafSettings,
      loading,
      selectedYear,
      setSelectedYear,
      fetchRecettes,
      fetchDepenses,
      fetchSummary,
      fetchComparison,
      fetchUrssafNotifications,
      updateUrssafSettings,
      markUrssafDeclared,
      createRecette,
      updateRecette,
      deleteRecette,
      createDepense,
      updateDepense,
      deleteDepense,
      exportCSV,
      exportJSON
    }}>
      {children}
    </TransactionContext.Provider>
  );
};

export const useTransactions = () => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactions must be used within a TransactionProvider');
  }
  return context;
};
